import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	Heading,
	HStack,
	Spacer,
	Stack,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Textarea,
	Tooltip,
} from '@chakra-ui/react';

import { getConsTabs } from '../app-data/consultations';
import { defaultDetails, defaultLink, options, timezone } from './data';
import {
	format,
	formatDate,
	formatDateNoTZ,
	formatNoTZ,
} from '../app-data/date';
import { ProfileSelect } from './elements/ProfileSelect';
import { VerRadioGroup } from './elements/VerRadioGroup';

export const Communications = ({ id, status, wake }) => {
	const [loading, setLoading] = useState(true);
	const [comms, setComms] = useState([]);
	const [cons, setCons] = useState([]);
	const [booked, setBooked] = useState([]);
	const [newComm, setNewComm] = useState('');
	const [fields, setFields] = useState({ tz: 'Europe/Moscow' });
	const [consID, setConsID] = useState(0);
	const [wakeComms, setWakeComms] = useState(new Date());

	const noComm = newComm.length < 1;

	const tzSelect = {
		...timezone,
		state: fields,
		setter: setFields,
	};

	useEffect(() => {
		let active = true;
		async function load() {
			const res = await axios.get(`/communications/${id}`, options());
			if (!active) {
				return;
			}
			setComms(res.data.communications);
			setCons(res.data.consultations);
			setBooked(res.data.booked);
			setLoading(false);
		}
		load();

		return () => {
			active = false;
		};
	}, [id, wake, wakeComms]);

	const handleCommChange = ({ target: { value } }) => setNewComm(value);
	const handleSend = () =>
		axios({
			method: 'post',
			url: `/communications`,
			data: {
				sender: id,
				customer: id,
				content: newComm,
			},
			...options(),
		})
			.then(() => {
				setComms([
					...comms,
					{
						sender_id: id,
						customer_id: id,
						content: newComm,
						sent_at: new Date().toString(),
					},
				]);
				setNewComm('');
			})
			.catch(console.log);

	if (loading) return <p>Loading...</p>;

	const consTabs = cons.reduce(getConsTabs, {});
	const consObject = cons.reduce((acc, i) => ({ ...acc, [i.id]: i }), {});
	const consultants = Object.keys(consTabs);

	const handleBooking = (consID, status) => {
		axios({
			method: 'put',
			url: `/cons`,
			data: { id: consID, customer: id, status, timezone: fields.tz },
			...options(),
		})
			.then(res => {
				if (typeof res.data.error === 'string')
					alert(
						'Выбранный слот был занят несколько секунд назад, подберите другой, пожалуйста.'
					);
				setConsID(0);
				setWakeComms(new Date());
			})
			.catch(console.log);
	};

	const handleCancel = e => {
		const { name } = e.target;
		// eslint-disable-next-line
		const ok = confirm('Please confirm cancelling consultation booking!');
		if (!ok) return;
		console.log('name', name);
		handleBooking(name, 'proposed');
	};
	return (
		<Stack align="top" direction={['column', 'row']}>
			{status === 'closed' ? (
				<Box
					w={['100%', '50%']}
					borderRadius="md"
					border="1px"
					borderColor="gray.200"
					p={4}
					mr={4}
				>
					<Heading pb={6} fontSize={'xl'}>
						Consultations booking disabled. Please contact staff
					</Heading>
				</Box>
			) : (
				<Box
					w={['100%', '50%']}
					borderRadius="md"
					border="1px"
					borderColor="gray.200"
					p={4}
					mr={4}
				>
					<Tooltip label="Забронировать консультацию" placement="top-start">
						<Heading pb={6} fontSize={'xl'}>
							Book consultation
						</Heading>
					</Tooltip>
					<Box>
						<Box pb={6}>
							<ProfileSelect {...tzSelect} />
						</Box>
						{booked.length > 0 ? (
							<Box>
								{booked.map(cons => (
									<Box
										borderWidth="1px"
										borderRadius="md"
										boxShadow="md"
										px={5}
										py={3}
									>
										<Heading pb={6} fontSize={'lg'}>
											{`${cons.consultant}, ${formatDateNoTZ(
												cons.starts_at,
												fields.tz
											)} (${fields.tz})`}
										</Heading>
										<Box>{cons.details || defaultDetails}</Box>
										<HStack>
											<Box w="80%">
												<a href={cons.link || defaultLink}>
													<Box fontWeight={700} color="blue.500">
														Link to conference
													</Box>
												</a>
											</Box>
											<Button
												d="block"
												w="20%"
												borderWidth="1px"
												borderRadius="md"
												boxShadow="md"
												colorScheme="red"
												height="52px"
												name={cons.id}
												onClick={handleCancel}
												py={3}
											>
												Cancel
											</Button>
										</HStack>
									</Box>
								))}
							</Box>
						) : (
							<>
								<Tabs pt={6} isFitted variant="enclosed">
									<TabList>
										{consultants.map(consultant => (
											<Tab>{consultant}</Tab>
										))}
									</TabList>

									<TabPanels>
										{consultants.map(consultant => {
											const dates = consTabs[consultant];
											return (
												<TabPanel>
													<Tabs variant="solid-rounded">
														<TabList overflowX="scroll" py={4}>
															{Object.keys(dates).map(date => (
																<Tab whiteSpace="nowrap">
																	{formatDate(date)}
																</Tab>
															))}
														</TabList>
														<TabPanels>
															{Object.keys(dates).map(date => {
																const hours = dates[date];

																return (
																	<TabPanel>
																		<VerRadioGroup
																			name="consType"
																			options={hours.reduce(
																				(acc, { id, starts_at }) => ({
																					...acc,
																					[id]: `${formatNoTZ(
																						starts_at,
																						fields.tz
																					)} (${fields.tz})`,
																				}),
																				{}
																			)}
																			onChange={v => setConsID(v)}
																		/>
																	</TabPanel>
																);
															})}
														</TabPanels>
													</Tabs>
												</TabPanel>
											);
										})}
									</TabPanels>
								</Tabs>
								<Box>
									<Button
										d="block"
										w="100%"
										colorScheme="blue"
										isDisabled={consID === 0}
										onClick={() => handleBooking(consID, 'booked')}
									>
										{consID === 0
											? 'Выберите консультанта и время'
											: `Записаться к ${
													consObject[consID].consultant
											  } на ${formatDateNoTZ(
													consObject[consID].starts_at,
													fields.tz
											  )} (${fields.tz})`}
									</Button>
								</Box>
							</>
						)}
					</Box>
				</Box>
			)}
			<Box
				w={['100%', '50%']}
				borderRadius="md"
				border="1px"
				borderColor="gray.200"
				p={4}
				mr={4}
			>
				<Tooltip label="Отправить сообщение" placement="top-start">
					<Heading pb={6} fontSize={'xl'}>
						Send message
					</Heading>
				</Tooltip>
				<Stack spacing={3}>
					{comms.map(({ sender_id, customer_id, sent_at, sender, content }) =>
						sender_id === customer_id ? (
							<Flex>
								<Box>&nbsp;</Box>
								<Spacer />
								<Box borderRadius="md" bgColor="teal.100" px={4}>
									<Box>{content}</Box>
									<Box fontSize="xs">{format(sent_at)}</Box>
								</Box>
							</Flex>
						) : (
							<Flex>
								<Box borderRadius="md" bgColor="blue.100" px={4}>
									<Box fontSize="sm" fontWeight="700">
										{sender}
									</Box>
									<Box>{content}</Box>
									<Box fontSize="xs">{format(sent_at)}</Box>
								</Box>
								<Spacer />
								<Box>&nbsp;</Box>
							</Flex>
						)
					)}
				</Stack>
				<Divider pb={6} />
				<Stack direction={['column', 'row']}>
					<FormControl>
						<Textarea value={newComm} onChange={handleCommChange} size="sm" />
					</FormControl>
					<Button colorScheme="blue" onClick={handleSend} disabled={noComm}>
						Send
					</Button>
				</Stack>
			</Box>
		</Stack>
	);
};
